/**
 * Тема: по умолчанию следует настройке системы, выбор пользователя сохраняется
 * (ТЗ §10.9). Явный выбор ставит data-theme на <html>, «системный» режим его
 * снимает — дальше работает prefers-color-scheme из tokens.css.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

export type ThemeChoice = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'ut-theme'

interface ThemeContextValue {
  choice: ThemeChoice
  setChoice: (next: ThemeChoice) => void
  /** Тема, которая фактически отображается прямо сейчас. */
  resolved: 'light' | 'dark'
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readStored(): ThemeChoice {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw
  } catch {
    // приватный режим или блокировка хранилища — работаем с системной темой
  }
  return 'system'
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement
  if (choice === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', choice)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStored)
  const [systemDark, setSystemDark] = useState(systemPrefersDark)

  useEffect(() => {
    applyTheme(choice)
  }, [choice])

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // выбор не сохранится, но интерфейс переключится
    }
  }, [])

  const resolved = choice === 'system' ? (systemDark ? 'dark' : 'light') : choice

  return (
    <ThemeContext.Provider value={{ choice, setChoice, resolved }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme требует ThemeProvider')
  return ctx
}
